/**
 * Call booking helpers — the browser half of framework/functions/call_booking.php.
 *
 * The Start Planning forms store the requested call date and time as UK
 * wall-clock time, because a UK team reads them. The guest, of course, picks in
 * their head in their own local time, which is how a guest in Los Angeles ended
 * up requesting slots that had already passed here.
 *
 * This module does two jobs:
 *
 *   1. Works out which dates and slots are actually bookable, in UK time, using
 *      the browser's own IANA timezone data rather than its raw clock offset —
 *      so BST/GMT transitions are handled for free.
 *   2. Converts every UK slot into the guest's local equivalent so the form can
 *      label it "10:00am UK / 2:00am your time" and the guest can see, before
 *      submitting, exactly what they are agreeing to.
 *
 * No dependencies, no side effects on import — safe to pull into any block.
 */

export const CALL_BOOKING_DEFAULTS = {
	timezone: 'Europe/London',
	timezoneLabel: 'UK',
	firstSlot: 8 * 60,
	lastSlot: 20 * 60 + 30,
	slotMinutes: 30,
	workingDays: [1, 2, 3, 4, 5],
	closedDates: ['12-25', '12-26', '01-01'],
	leadDays: 1,
	earliestDate: null,
	latestDate: null,
	windowDays: 14,
	defaultSlot: '',
	defaultLocalMinutes: 600,
	geoTimezone: true,
};

/**
 * A value the GF time field stores: "8:00am", "12:30pm".
 */
const TIME_VALUE_PATTERN = /^\d{1,2}:\d{2}\s?(am|pm)$/i;

/**
 * Does this select hold clock times?
 *
 * Used to find the call time field by what it contains rather than by id or
 * class. Both of those are editable in the Gravity Forms editor and neither is
 * worth pinning a booking rule to.
 */
export function looksLikeTimeSelect(select) {
	if (!select || select.tagName !== 'SELECT') {
		return false;
	}

	const values = Array.from(select.options)
		.map((option) => option.value)
		.filter((value) => value !== '');

	return values.length >= 2 && values.every((value) => TIME_VALUE_PATTERN.test(value.trim()));
}

/**
 * Locate the call date and time controls inside a rendered form.
 *
 * Everything here keys off Gravity Forms' own structural markup and the field
 * contents — never a field id, a form id or an editor-set CSS class:
 *
 *   - the date field is the one GF marks as a date field (or, failing that, the
 *     field wrapping GF's date-dropdown/date-container markup);
 *   - its day/month/year selects are found by GF's own dropdown classes, so the
 *     field's configured date order doesn't matter;
 *   - the time field is the select on the same page whose options are all clock
 *     times.
 *
 * @param {Element|null} root
 * @returns {{container:Element|null,day:HTMLSelectElement|null,month:HTMLSelectElement|null,year:HTMLSelectElement|null,picker:HTMLInputElement|null,time:HTMLSelectElement|null}}
 */
export function findCallBookingFields(root) {
	const empty = { container: null, day: null, month: null, year: null, picker: null, time: null };

	if (!root) {
		return empty;
	}

	let container = root.querySelector('.gfield--type-date');

	if (!container) {
		// Legacy GF markup mode doesn't emit the gfield--type-* classes.
		const marker = root.querySelector('.gfield_date_dropdown_day, .ginput_container_date');
		container = marker ? marker.closest('.gfield, li') : null;
	}

	// Prefer a time select on the same page as the date field; fall back to the
	// whole form in case the two ever end up on separate steps.
	const page = container ? container.closest('.gform_page') : null;
	const time =
		(page ? Array.from(page.querySelectorAll('select')).find(looksLikeTimeSelect) : null) ||
		Array.from(root.querySelectorAll('select')).find(looksLikeTimeSelect) ||
		null;

	if (!container && !time) {
		return empty;
	}

	return {
		container,
		time,
		day: container ? container.querySelector('.gfield_date_dropdown_day select') : null,
		month: container ? container.querySelector('.gfield_date_dropdown_month select') : null,
		year: container ? container.querySelector('.gfield_date_dropdown_year select') : null,
		picker: container ? container.querySelector('input.datepicker, .ginput_container_date input[type="text"]') : null,
	};
}

/**
 * Read the window config PHP rendered onto the block wrapper. Falls back to the
 * first config in the document (the datepicker filter runs at file scope, with
 * no component root to hand us) and then to the defaults above.
 *
 * @param {Element|null} root
 * @returns {typeof CALL_BOOKING_DEFAULTS}
 */
export function readCallBookingConfig(root) {
	let raw = root && root.dataset ? root.dataset.callBookingConfig : null;

	if (!raw) {
		const host = document.querySelector('[data-call-booking-config]');
		raw = host ? host.dataset.callBookingConfig : null;
	}

	if (!raw) {
		return { ...CALL_BOOKING_DEFAULTS };
	}

	try {
		const parsed = JSON.parse(raw);

		if (!parsed || typeof parsed !== 'object') {
			return { ...CALL_BOOKING_DEFAULTS };
		}

		return { ...CALL_BOOKING_DEFAULTS, ...parsed };
	} catch (error) {
		return { ...CALL_BOOKING_DEFAULTS };
	}
}

/**
 * Minutes to add to UTC to get wall-clock time in `timeZone` at `instant`.
 *
 * Derived from Intl rather than arithmetic, so it is correct across DST changes
 * and needs no timezone table of our own.
 *
 * @param {string} timeZone
 * @param {Date} instant
 * @returns {number}
 */
export function zoneOffsetMinutes(timeZone, instant) {
	try {
		const formatter = new Intl.DateTimeFormat('en-GB', {
			timeZone,
			hourCycle: 'h23',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});

		const parts = {};
		formatter.formatToParts(instant).forEach((part) => {
			if (part.type !== 'literal') {
				parts[part.type] = part.value;
			}
		});

		const asUTC = Date.UTC(
			Number(parts.year),
			Number(parts.month) - 1,
			Number(parts.day),
			Number(parts.hour),
			Number(parts.minute),
			Number(parts.second)
		);

		return Math.round((asUTC - (instant.getTime() - instant.getMilliseconds())) / 60000);
	} catch (error) {
		return -instant.getTimezoneOffset();
	}
}

/**
 * The instant at which the clock in `timeZone` reads the given wall time.
 *
 * The first offset lookup uses a naive guess, which can land on the wrong side
 * of a DST boundary; the second pass corrects it.
 *
 * @returns {Date}
 */
export function zonedWallToInstant(timeZone, year, month, day, minutesPastMidnight) {
	const naive = Date.UTC(year, month - 1, day, 0, 0, 0) + minutesPastMidnight * 60000;

	let offset = zoneOffsetMinutes(timeZone, new Date(naive));
	let instant = new Date(naive - offset * 60000);

	const corrected = zoneOffsetMinutes(timeZone, instant);
	if (corrected !== offset) {
		instant = new Date(naive - corrected * 60000);
	}

	return instant;
}

/**
 * Wall-clock parts in `timeZone` for a given instant.
 *
 * @returns {{year:number,month:number,day:number,minutes:number}}
 */
export function zonedParts(timeZone, instant) {
	const offset = zoneOffsetMinutes(timeZone, instant);
	const shifted = new Date(instant.getTime() - instant.getMilliseconds() + offset * 60000);

	return {
		year: shifted.getUTCFullYear(),
		month: shifted.getUTCMonth() + 1,
		day: shifted.getUTCDate(),
		minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
	};
}

/**
 * The timezone the guest's own device is set to. Empty string if the browser
 * will not say.
 */
export function browserTimeZone() {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
	} catch (error) {
		return '';
	}
}

/** Will Intl accept this as a timezone? Guards anything we didn't compute. */
export function isValidTimeZone(timeZone) {
	if (typeof timeZone !== 'string' || timeZone === '') {
		return false;
	}

	try {
		new Intl.DateTimeFormat('en-GB', { timeZone });
		return true;
	} catch (error) {
		return false;
	}
}

/*
 * Where the guest actually is, as opposed to how their laptop is set.
 *
 * The phone field already geolocates by IP — the international phone add-on
 * calls ipinfo.io to pick the dial code — so the form was telling the guest two
 * different stories: a US flag on the phone field, UK-only times below it. That
 * same response carries an IANA `timezone`, so we reuse it and the two agree.
 *
 * IP is not automatically the better signal: a guest on a VPN or a corporate
 * proxy looks like they are somewhere they are not, while the device clock is
 * the one they will actually read. But a form that contradicts itself is worse,
 * and the note names the place ("your time in New York, EDT") so a wrong guess
 * is visible rather than silent.
 *
 * Best-effort throughout — never awaited before first paint, and any failure
 * (ad-blocker, no token, timeout) leaves the device zone in charge.
 */
const GEO_SESSION_KEY = 'pegasusCallBookingGuestGeo';
const GEO_TIMEOUT_MS = 2500;

let geo = null;
let geoPromise = null;

/** Keep only the fields we use, and only if they're strings. */
function tidyGeo(data) {
	if (!data || typeof data !== 'object') {
		return null;
	}

	const pick = (value) => (typeof value === 'string' ? value.trim() : '');
	const tidied = {
		timeZone: isValidTimeZone(data.timezone) ? data.timezone : '',
		city: pick(data.city),
		region: pick(data.region),
		country: pick(data.country),
	};

	return tidied.timeZone || tidied.region || tidied.city ? tidied : null;
}

/** What the IP lookup told us — from this page, or earlier this session. */
export function guestGeo() {
	if (!geo) {
		try {
			geo = tidyGeo(JSON.parse(window.sessionStorage.getItem(GEO_SESSION_KEY) || 'null'));
		} catch (error) {
			// Storage disabled or unparseable; the in-memory copy is enough.
		}
	}

	return geo;
}

/**
 * The guest's timezone right now: the IP answer if we have one, otherwise the
 * device's. Synchronous, so it is safe to call during render.
 */
export function guestTimeZone() {
	const known = guestGeo();

	return (known && known.timeZone) || browserTimeZone();
}

/**
 * Where the IP says the guest is, in words, for the Location field — the most
 * recognisable form we have. '' when we don't know.
 */
export function guestPlace() {
	const known = guestGeo();

	if (!known) {
		return '';
	}

	return [known.city, known.region].filter(Boolean).join(', ') || known.country;
}

/**
 * Ask ipinfo.io where the guest is, reusing the token the phone add-on already
 * localises. Resolves to the tidied geo object, or null if we could not find
 * out. Never rejects — nothing here is worth breaking the form over — and runs
 * at most once per session.
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 * @returns {Promise<object|null>}
 */
export function lookupGuestGeo(config) {
	if (!geoPromise) {
		geoPromise = requestGeo(config);
	}

	return geoPromise;
}

function requestGeo(config) {
	const token = typeof window !== 'undefined' && window.token_geo ? String(window.token_geo) : '';
	const unavailable = (config && config.geoTimezone === false) || token === '' || typeof fetch !== 'function';

	if (unavailable || guestGeo()) {
		return Promise.resolve(guestGeo());
	}

	const controller = typeof AbortController === 'function' ? new AbortController() : null;
	const timer = window.setTimeout(() => controller && controller.abort(), GEO_TIMEOUT_MS);

	return fetch(`https://ipinfo.io/json?token=${encodeURIComponent(token)}`, {
		signal: controller ? controller.signal : undefined,
		headers: { Accept: 'application/json' },
	})
		.then((response) => (response.ok ? response.json() : null))
		.then((data) => {
			const tidied = tidyGeo(data);

			if (tidied) {
				geo = tidied;

				try {
					window.sessionStorage.setItem(GEO_SESSION_KEY, JSON.stringify(tidied));
				} catch (error) {
					// Storage disabled; the in-memory copy still serves this page.
				}
			}

			return tidied;
		})
		.catch(() => null)
		.finally(() => window.clearTimeout(timer));
}

/**
 * A short, human name for a timezone at a given instant — "BST", "PDT",
 * "GMT+2". Used in the note under the fields, never in a stored value.
 *
 * Intl abbreviates a zone only for locales that use it, so the locale matters:
 * en-GB renders London as "BST" but New York as "GMT-4". Pass the guest's own
 * locale (undefined = the browser's) when naming the guest's zone, so an
 * American reads "EDT" rather than an offset.
 */
export function timeZoneAbbreviation(timeZone, instant, locale = 'en-GB') {
	try {
		const formatter = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' });
		const part = formatter.formatToParts(instant).find((candidate) => candidate.type === 'timeZoneName');

		return part ? part.value : '';
	} catch (error) {
		return '';
	}
}

/**
 * The abbreviation to show for the guest's own zone.
 *
 * Intl only abbreviates a zone for locales that actually use it: en-GB renders
 * New York as "GMT-4", en-US as "EDT". So we try the guest's own locale first —
 * which is how an Australian gets "AEST" and an Indian "IST" — then a couple of
 * others before settling for a bare offset.
 */
export function guestZoneAbbreviation(timeZone, instant) {
	// The IP country first — "en-AU" is what gets Sydney named "AEST" instead of
	// "GMT+10" — because it names the region the zone actually belongs to. The
	// browser's own locale is not reliable for this: plenty of browsers and OSes
	// report a generic "en-GB" regardless of where the device physically is, so
	// a real Australian guest was still landing on a bare offset. Device locale
	// and the two broadest English locales stay as fallbacks for whoever we have
	// no IP country for.
	const geoCountry = (guestGeo() && guestGeo().country) || '';
	const candidates = [geoCountry ? `en-${geoCountry}` : null, undefined, 'en-US', 'en-GB'];

	for (const locale of candidates) {
		if (locale === null) {
			continue;
		}

		const abbreviation = timeZoneAbbreviation(timeZone, instant, locale);

		if (abbreviation !== '' && !/^(GMT|UTC)/i.test(abbreviation)) {
			return abbreviation;
		}
	}

	// Nothing named it, so a plain offset it is.
	return timeZoneAbbreviation(timeZone, instant, undefined);
}

/** "America/Los_Angeles" reads better as "Los Angeles". */
export function prettyTimeZoneName(timeZone) {
	if (typeof timeZone !== 'string' || timeZone === '') {
		return '';
	}

	const segments = timeZone.split('/');

	return segments[segments.length - 1].replace(/_/g, ' ');
}

/** "8:00am" -> 480. Returns null for anything unreadable. */
export function slotToMinutes(value) {
	if (typeof value !== 'string') {
		return null;
	}

	const normalised = value.toLowerCase().replace(/[\s.]/g, '');
	let match = normalised.match(/^(\d{1,2}):(\d{2})(am|pm)$/);

	if (match) {
		const minutes = Number(match[2]);
		let hours = Number(match[1]) % 12;

		if (match[3] === 'pm') {
			hours += 12;
		}

		return hours * 60 + minutes;
	}

	match = normalised.match(/^(\d{1,2}):(\d{2})$/);

	if (match) {
		const hours = Number(match[1]);
		const minutes = Number(match[2]);

		if (hours > 23 || minutes > 59) {
			return null;
		}

		return hours * 60 + minutes;
	}

	return null;
}

/** 480 -> "8:00am". Matches the format the GF time field stores. */
export function minutesToSlot(minutes) {
	const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
	const hours24 = Math.floor(wrapped / 60);
	const mins = wrapped % 60;
	const suffix = hours24 < 12 ? 'am' : 'pm';
	const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

	return `${hours12}:${String(mins).padStart(2, '0')}${suffix}`;
}

/** Y-m-d, from wall-clock parts. */
export function toIsoDate(year, month, day) {
	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Day of week for a wall-clock date, ISO-8601 numbered (1 = Monday). */
export function isoWeekday(year, month, day) {
	const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

	return weekday === 0 ? 7 : weekday;
}

/**
 * Does the team take calls on this date?
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 */
export function isOpenOn(config, year, month, day) {
	if (!config.workingDays.includes(isoWeekday(year, month, day))) {
		return false;
	}

	const monthDay = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

	return !config.closedDates.includes(monthDay);
}

/**
 * The earliest bookable date, as Y-m-d in site time.
 *
 * Computed from the browser's clock converted into the site timezone, then
 * floored by the value PHP rendered. Whichever is later wins: a cached page
 * cannot reopen a date that has passed, and a browser clock running slow cannot
 * either.
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 * @param {Date} [now]
 */
export function earliestBookableDate(config, now = new Date()) {
	const today = zonedParts(config.timezone, now);

	let cursor = new Date(Date.UTC(today.year, today.month - 1, today.day));
	let remaining = Math.max(0, config.leadDays);

	// Step forward one calendar day at a time, only counting days the team is
	// open, until the lead requirement is met and we land on an open day.
	for (let guard = 0; guard < 40; guard++) {
		if (remaining > 0) {
			cursor = new Date(cursor.getTime() + 86400000);

			if (isOpenOn(config, cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, cursor.getUTCDate())) {
				remaining -= 1;
			}

			continue;
		}

		if (isOpenOn(config, cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, cursor.getUTCDate())) {
			break;
		}

		cursor = new Date(cursor.getTime() + 86400000);
	}

	const computed = toIsoDate(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, cursor.getUTCDate());
	const floor = typeof config.earliestDate === 'string' ? config.earliestDate : '';

	return floor > computed ? floor : computed;
}

/**
 * The last bookable date, as Y-m-d in site time: `windowDays` from the earliest
 * bookable date, counting that date itself.
 *
 * Mirrors pegasus_call_booking_latest_date() exactly — derived purely from
 * earliestBookableDate(), with no separate ceiling of its own. There used to
 * be one, clamped to config.latestDate whenever that was earlier, on the
 * reasoning that "the server's value wins when it's earlier, so a stale page
 * can't open a date the server would now reject". That reasoning doesn't hold
 * for a ceiling the way it does for earliestBookableDate's floor: latestDate
 * is never an independent policy, it's always "the earliest date plus the
 * window", computed once at render time — so once any real time has passed
 * since then, it is *always* earlier than a freshly-computed value, and
 * clamping to it pins the window's end to a point before its own (correctly
 * advanced) start. That's exactly what left this guest unable to pick
 * anything at all once their session crossed into a new render's window.
 * earliestBookableDate() already guards against a stale-in-the-past page on
 * the low end; deriving latest purely from that earliest carries the same
 * guarantee through without a second, conflicting one.
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 * @param {Date} [now]
 */
export function latestBookableDate(config, now = new Date()) {
	const [year, month, day] = earliestBookableDate(config, now).split('-').map(Number);
	const span = Math.max(1, config.windowDays);

	const end = new Date(Date.UTC(year, month - 1, day + span - 1));

	return toIsoDate(end.getUTCFullYear(), end.getUTCMonth() + 1, end.getUTCDate());
}

/**
 * Is this date inside the bookable window — not past, not too far ahead, and a
 * day the team is open?
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 */
export function isBookableDate(config, isoDate, now = new Date()) {
	const [year, month, day] = isoDate.split('-').map(Number);

	return (
		isoDate >= earliestBookableDate(config, now) &&
		isoDate <= latestBookableDate(config, now) &&
		isOpenOn(config, year, month, day)
	);
}

/**
 * Every slot in the window, chronological.
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 * @returns {Array<{minutes:number,value:string}>}
 */
export function slotsInWindow(config) {
	const step = Math.max(1, config.slotMinutes);
	const slots = [];

	for (let minutes = config.firstSlot; minutes <= config.lastSlot; minutes += step) {
		slots.push({ minutes, value: minutesToSlot(minutes) });
	}

	return slots;
}

/**
 * Everything the UI needs to describe one slot in both timezones.
 *
 * `dayShift` is -1, 0 or +1: how the guest's local date relates to the UK date,
 * which matters a great deal in Sydney and not at all in Dublin.
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 * @param {string} isoDate Y-m-d, in site time
 * @param {number} minutes minutes past midnight, in site time
 */
export function describeSlot(config, isoDate, minutes, zone) {
	const [year, month, day] = isoDate.split('-').map(Number);
	const instant = zonedWallToInstant(config.timezone, year, month, day, minutes);

	const siteLabel = minutesToSlot(minutes);
	const guestZone = typeof zone === 'string' && zone !== '' ? zone : guestTimeZone();

	// Derive the guest's wall clock from their zone, not from this device's
	// clock, so an IP-derived zone describes the guest rather than the browser.
	// Only when we have no usable zone at all does the device's own clock stand
	// in for it.
	const local = isValidTimeZone(guestZone)
		? zonedParts(guestZone, instant)
		: {
				year: instant.getFullYear(),
				month: instant.getMonth() + 1,
				day: instant.getDate(),
				minutes: instant.getHours() * 60 + instant.getMinutes(),
			};

	const localMinutes = local.minutes;
	const localIso = toIsoDate(local.year, local.month, local.day);

	let dayShift = 0;
	if (localIso > isoDate) {
		dayShift = 1;
	} else if (localIso < isoDate) {
		dayShift = -1;
	}

	return {
		instant,
		siteLabel,
		siteMinutes: minutes,
		localLabel: minutesToSlot(localMinutes),
		localMinutes,
		localIsoDate: localIso,
		dayShift,
		guestZone,
		// True when the guest is effectively on UK time, in which case showing a
		// second, identical time is just noise.
		matchesSite:
			zoneOffsetMinutes(config.timezone, instant) ===
			(isValidTimeZone(guestZone) ? zoneOffsetMinutes(guestZone, instant) : -instant.getTimezoneOffset()),
	};
}

/**
 * The option label: the time in the guest's own timezone, which is the only
 * time they should have to think about.
 *
 * The stored value stays UK time — that's what the team dials at and what
 * reaches HubSpot — so a guest in Boston picking "5:00pm EDT" sends us 10:00pm
 * UK. Showing both was the earlier design and read as clutter; one time in the
 * guest's own zone is what the client asked for.
 *
 * e.g. "5:00pm EDT", or "8:00am AEST (next day)" for a guest far enough east.
 */
export function slotOptionLabel(config, slot) {
	const zone = slot.matchesSite
		? timeZoneAbbreviation(config.timezone, slot.instant)
		: guestZoneAbbreviation(slot.guestZone, slot.instant);

	let suffix = '';
	if (slot.dayShift === 1) {
		suffix = ' (next day)';
	} else if (slot.dayShift === -1) {
		suffix = ' (prev day)';
	}

	return `${slot.localLabel}${zone ? ` ${zone}` : ''}${suffix}`;
}

/**
 * The slot whose time in the guest's own zone sits closest to `targetMinutes`
 * (10am by default), out of the slots we can actually offer on `isoDate`.
 *
 * Wherever the guest is, this is the one the form should pre-select. For a guest
 * in Sydney no slot lands anywhere near their 10am, so "closest" is doing real
 * work rather than just finding an exact match.
 *
 * @param {typeof CALL_BOOKING_DEFAULTS} config
 * @returns {{minutes:number,value:string}|null}
 */
export function slotNearestLocalTime(config, isoDate, targetMinutes, now = new Date()) {
	const nowMs = now.getTime();
	let best = null;
	let bestDistance = Infinity;

	slotsInWindow(config).forEach((slot) => {
		const described = describeSlot(config, isoDate, slot.minutes);

		// Never pre-select something we'd then reject.
		if (described.instant.getTime() <= nowMs) {
			return;
		}

		const gap = Math.abs(described.localMinutes - targetMinutes);
		const distance = Math.min(gap, 1440 - gap);

		if (distance < bestDistance) {
			best = slot;
			bestDistance = distance;
		}
	});

	return best;
}

const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The booked call in the guest's own time, for the hidden field the entry and
 * HubSpot read: e.g. "10:00am EDT, Tue 29 Sep (America/New_York)".
 *
 * Always carries the guest's own date, because the date field stores the UK
 * date and the two part ways for late-evening UK slots — 11:00pm UK is the
 * next morning in Sydney. The IANA zone goes last so the team can tell EST
 * from AEST at a glance.
 *
 * @param {ReturnType<typeof describeSlot>} slot
 */
export function guestTimeDescription(slot) {
	const [year, month, day] = slot.localIsoDate.split('-').map(Number);
	const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

	// Spelled out rather than left to Intl, which writes "Sept" in some
	// browsers — this sits next to PHP's "D j M" in HubSpot, so they must match.
	const date = `${SHORT_WEEKDAYS[weekday]} ${day} ${SHORT_MONTHS[month - 1]}`;
	const zone = guestZoneAbbreviation(slot.guestZone, slot.instant);

	return `${slot.localLabel}${zone ? ` ${zone}` : ''}, ${date}${slot.guestZone ? ` (${slot.guestZone})` : ''}`;
}
